/* ============================================================
   sl-scroll-carousel.js
   - Auto mode: clones the track contents once for a seamless
     loop. Editor attributes are stripped from clones so the
     Theme Editor only "sees" the original blocks.
   - Manual mode: adds mouse-drag support, prev/next arrow
     navigation, pagination dots, and a scroll observer that
     keeps the active dot + arrow disabled state in sync with
     the viewport's scroll position. Touch swipe is handled
     natively by overflow-x scrolling.
   - Idempotent: safe to re-run on shopify:section:load.
   ============================================================ */
(function () {
  'use strict';

  var EDITOR_ATTRS = [
    'data-shopify-editor-block',
    'data-shopify-editor-block-id',
    'data-shopify-block-id'
  ];

  function stripEditorAttrs(node) {
    EDITOR_ATTRS.forEach(function (attr) {
      if (node.hasAttribute && node.hasAttribute(attr)) node.removeAttribute(attr);
    });
    var nested = node.querySelectorAll ? node.querySelectorAll('*') : [];
    for (var i = 0; i < nested.length; i++) {
      EDITOR_ATTRS.forEach(function (attr) {
        if (nested[i].hasAttribute(attr)) nested[i].removeAttribute(attr);
      });
    }
  }

  function setupAutoMarquee(viewport) {
    var track = viewport.querySelector('[data-sl-track]');
    if (!track) return;

    /* Remove any existing clones from a previous render */
    var existingClones = track.querySelectorAll('[data-sl-clone]');
    for (var i = 0; i < existingClones.length; i++) {
      existingClones[i].parentNode.removeChild(existingClones[i]);
    }

    /* Original children = anything not flagged as a clone */
    var originals = [];
    for (var j = 0; j < track.children.length; j++) {
      if (!track.children[j].hasAttribute('data-sl-clone')) {
        originals.push(track.children[j]);
      }
    }
    if (originals.length === 0) return;

    /* Clone each original, strip editor attributes, mark and append */
    for (var k = 0; k < originals.length; k++) {
      var clone = originals[k].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-sl-clone', '');
      stripEditorAttrs(clone);
      track.appendChild(clone);
    }

    viewport.classList.add('is-ready');
  }

  /* Pause the marquee animation while the section is offscreen.
     CSS reads .is-offscreen on the viewport (paired with .is-paused
     in the same selector) so the animation doesn't burn frames the
     user can't see. The carousel starts in its running state — we
     only ADD .is-offscreen when the observer reports the section
     has left the viewport. Pre-adding the class on init created a
     "paused → IO callback resumes it one or two frames later" race
     that made on-arrival animations and scroll input feel stuck. */
  function setupViewportGate(viewport) {
    if (viewport.dataset.slIoInit === 'true') return;
    viewport.dataset.slIoInit = 'true';

    if (typeof IntersectionObserver === 'undefined') {
      /* No IO support — leave animation running, fail open. */
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          viewport.classList.remove('is-offscreen');
        } else {
          viewport.classList.add('is-offscreen');
        }
      }
    }, { rootMargin: '200px 0px', threshold: 0 });

    io.observe(viewport);
  }

  function setupManualDrag(viewport) {
    if (viewport.dataset.slDragInit === 'true') return;
    viewport.dataset.slDragInit = 'true';

    var isDown = false;
    var startX = 0;
    var startScroll = 0;

    viewport.addEventListener('mousedown', function (e) {
      isDown = true;
      viewport.classList.add('is-dragging');
      startX = e.pageX - viewport.offsetLeft;
      startScroll = viewport.scrollLeft;
    });
    viewport.addEventListener('mouseleave', function () {
      isDown = false;
      viewport.classList.remove('is-dragging');
    });
    viewport.addEventListener('mouseup', function () {
      isDown = false;
      viewport.classList.remove('is-dragging');
    });
    viewport.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      e.preventDefault();
      var x = e.pageX - viewport.offsetLeft;
      var walk = (x - startX) * 1.5;
      viewport.scrollLeft = startScroll - walk;
    });
  }

  /* Returns the horizontal step (one card width + gap), measured
     from the actual rendered DOM so it adapts to live setting
     changes without re-reading the schema. */
  function getCardStep(viewport) {
    var track = viewport.querySelector('[data-sl-track]');
    if (!track) return 0;
    var firstCard = track.firstElementChild;
    if (!firstCard) return 0;
    var gap = parseFloat(getComputedStyle(track).columnGap) || parseFloat(getComputedStyle(track).gap) || 0;
    return firstCard.offsetWidth + gap;
  }

  function setupManualControls(viewport) {
    if (viewport.dataset.slCtrlInit === 'true') return;
    viewport.dataset.slCtrlInit = 'true';

    /* Arrows + dots are siblings of the viewport, scoped to the
       same section root. Find them by walking up to the nearest
       carousel wrapper. */
    var carousel = viewport.parentElement;
    var sectionRoot = viewport.closest('[id^="sl-section-"]');
    if (!carousel || !sectionRoot) return;

    var prevBtn = carousel.querySelector('[data-sl-prev]');
    var nextBtn = carousel.querySelector('[data-sl-next]');
    var dots = sectionRoot.querySelectorAll('[data-sl-dot-index]');

    function updateState() {
      var max = viewport.scrollWidth - viewport.clientWidth;
      var here = viewport.scrollLeft;
      if (prevBtn) prevBtn.disabled = here <= 0;
      if (nextBtn) nextBtn.disabled = here >= max - 1;

      if (dots.length) {
        var step = getCardStep(viewport);
        var idx = step > 0 ? Math.round(here / step) : 0;
        if (idx < 0) idx = 0;
        if (idx > dots.length - 1) idx = dots.length - 1;
        for (var i = 0; i < dots.length; i++) {
          if (i === idx) {
            dots[i].classList.add('is-active');
          } else {
            dots[i].classList.remove('is-active');
          }
        }
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        var step = getCardStep(viewport);
        if (step > 0) viewport.scrollBy({ left: -step, behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        var step = getCardStep(viewport);
        if (step > 0) viewport.scrollBy({ left: step, behavior: 'smooth' });
      });
    }
    for (var i = 0; i < dots.length; i++) {
      (function (idx, dot) {
        dot.addEventListener('click', function () {
          var step = getCardStep(viewport);
          if (step > 0) viewport.scrollTo({ left: idx * step, behavior: 'smooth' });
        });
      })(i, dots[i]);
    }

    /* rAF-throttled scroll listener — keeps arrows + dots in sync
       with both programmatic scrolls and native swipes/drags. */
    var rafId = null;
    viewport.addEventListener('scroll', function () {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(function () {
        rafId = null;
        updateState();
      });
    });

    /* Re-measure on resize (card width is responsive). */
    window.addEventListener('resize', function () {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(function () {
        rafId = null;
        updateState();
      });
    });

    updateState();
  }

  function initViewport(viewport) {
    var mode = viewport.dataset.mode;
    if (mode === 'auto') {
      setupAutoMarquee(viewport);
      setupViewportGate(viewport);
    } else if (mode === 'manual') {
      setupManualDrag(viewport);
      setupManualControls(viewport);
    }
  }

  function initAll(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-sl-carousel]');
    for (var i = 0; i < nodes.length; i++) initViewport(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme Editor: re-init when the section is added or reloaded */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    /* Pause auto-scroll briefly so the selected block is reachable */
    var viewport = e.target.closest && e.target.closest('[data-sl-carousel][data-mode="auto"]');
    if (viewport) viewport.classList.add('is-paused');
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var viewport = e.target.closest && e.target.closest('[data-sl-carousel][data-mode="auto"]');
    if (viewport) viewport.classList.remove('is-paused');
  });
})();
